import React, { FC, useEffect, useState } from "react"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
import { RouteComponentProps, Link, navigate, Router } from "@reach/router"
// Components
import { Toolbar } from "components/subnav"
import { TabView, TabPanel } from 'primereact/tabview';
import AssetSettings from "components/asset-management/asset-list/detail/asset-settings"
import AssetAttributes from "components/asset-management/asset-list/detail/attributes"
import { Errors } from "components/asset-management/errors"
// Data
import api_match from "data/interface/api"
import assetService from "data/asset-list"
import propertyAssetService from "data/property-asset"
import assetAttributeService from "data/asset-attribute"
import chartService from "data/chart"
import attributeService from "data/attributes"
import keyService, * as chord from "data/key"
import validationService from "data/validate"
// Styles
import { HeaderIcon_s } from 'components/wrapper/header/header-styles'
import { Tabs_s } from 'styles/tab-styles'
import { DetailedRouteUpDown_s, DetailedUpDownBtn_s } from "components/table/data-table-styles"
// Icons
import { CaretRightIcon, BackArrowIcon } from 'images/icons/general/'
import { CaretDownIcon, CaretUpIcon } from "images/icons/tools/"

type Asset = { assetid: number }
const AssetListDetail: FC<RouteComponentProps & {assetId?: number}> = (props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [assets, setAssets] = useState<Asset[] | null>(null)
  const [assetattributes, setAssetAttributes] = useState<object[]>([])
  const [attributes, setAttributes] = useState<object[]>([])
  const [attributedata, setAttributeData] = useState<object[]>([])
  const [charts, setCharts] = useState<object[]>([]);
  const assetid = +(props.assetId!) 
  const currentAsset = _.find(assets, {assetid})
  const readonly = window.location.pathname.startsWith('/n/property/asset-info')


  useEffect(() => {
    const assetSubscription = assetService.stream().subscribe(data => {
      api_match(
        data,
        init  => {},
        ok    => {
          setAssets(data.value as Asset[])
        },
        err   => {},
        fetch => {}
      )
    })

    const assetAttributeSubscription = assetAttributeService.stream().subscribe(data => {
      api_match(
        data,
        init  => {},
        ok    => {
          setAssetAttributes(data.value)
        },
        err   => {},
        fetch => {}
      )
    })

    const chartSubscription = chartService.stream().subscribe(data => {
      if (data.state === "ok"){
        setCharts(data.value)
      }
    })

    const attributeSubscription = attributeService.stream().subscribe(data => {
      api_match(
        data,
        init  => {},
        ok    => {
          setAttributes(data.value)
        },
        err   => {},
        fetch => {}
      )
    })

    assetService.fetchAssets()
    chartService.fetchCharts()
    attributeService.fetchAttributes()

    return function cleanup() {
      assetSubscription.unsubscribe()
      assetAttributeSubscription.unsubscribe()
      chartSubscription.unsubscribe()
      attributeSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const propertyAssetSubscription = propertyAssetService.stream().subscribe(data => {
      if (data === undefined) return
      if (!readonly) return
      if (data === null) return window.location.assign(`/property/settings`)
      if (data !== assetid) navigate(`/n/property/asset-info/${data}`)
    })
    return function cleanup() {
      propertyAssetSubscription.unsubscribe()
    }
  }, [assetid])

  useEffect(() => {
    assetAttributeService.fetch(assetid)
  }, [assetid])

  useEffect(() => {
    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_BACK && k.down === true:
          navigate('/n/asset-management/asset-list')
          break
        case k.chord === chord.CTRL_SHIFT_LEFT && k.down === true:
          setActiveIndex(activeIndex === 0 ? 1 : 0)
        break
        case k.chord === chord.CTRL_SHIFT_RIGHT && k.down === true:
          setActiveIndex(activeIndex === 0 ? 1 : 0)
          break
        case k.chord === chord.CTRL_SHIFT_UP && k.down === true:
          toggleAsset('up')
          break
        case k.chord === chord.CTRL_SHIFT_DOWN && k.down === true:
          toggleAsset('down')
          break
      }
    })

    return function cleanup() {
      keySubscription.unsubscribe()
    }
  })

  useEffect(() => {
    setAttributeData(
      _.map(attributes, (a) => ({
        assetid,
        ...a,
        ..._.find(assetattributes, {assetid, attributeid: a.attributeid})
      }))
    )
  }, [attributes, assetattributes, currentAsset])

  function toggleAsset(direction:string):void {
    if (!assets) return
    const currentAssetIdx = assets.indexOf(currentAsset!);
    const step = direction == 'up' ? -1 : 1
    const nextAsset = _.get(assets, currentAssetIdx + step)
    if (nextAsset) {
      navigate(`/n/asset-management/asset-list/${nextAsset.assetid}`)
    }
  }

  useEffect(() => {
    const filter = (errors) => {
      if (!currentAsset) return errors
      let { asset, assetattribute } = _.cloneDeep(_.pick(errors.errordetail, 'asset', 'assetattribute'))
      if (_.get(asset, assetid)) asset = {[assetid]: asset[assetid]}
      if (assetattribute) {
        for (let id in assetattribute){
          if (assetattribute[id][0]['_object'].assetid !== assetid) {
            delete assetattribute[id]
          }
        }
      }
      return {
        ...errors,
        errordetail: {asset, assetattribute},
        calcDetail: undefined
      }
    }
    validationService.addFilter(filter)
    return () => validationService.removeFilter(filter)
  }, [currentAsset])

  return (
    <div>
      <Toolbar>
        { !readonly && <div>
          <Link to="/n/asset-management/asset-list">
            <HeaderIcon_s style={{marginRight: '5px'}}><Isvg src={BackArrowIcon} /></HeaderIcon_s>
            Asset List
          </Link>
          <HeaderIcon_s style={{marginRight: '5px'}}><Isvg src={CaretRightIcon} /></HeaderIcon_s>
          <div>Information/Attributes</div>
          <DetailedRouteUpDown_s>
            <DetailedUpDownBtn_s id="detailedRouteRecordUp" disabled={!assets || _.isNil(assets[assets.indexOf(currentAsset!) - 1])} onClick={()=>toggleAsset('up')}><Isvg src={CaretUpIcon} /></DetailedUpDownBtn_s>
            <DetailedUpDownBtn_s id="detailedRouteRecordDown" disabled={!assets || _.isNil(assets[assets.indexOf(currentAsset!) + 1])} onClick={()=>toggleAsset('down')}><Isvg src={CaretDownIcon} /></DetailedUpDownBtn_s>
          </DetailedRouteUpDown_s>
          <div>{_.get(currentAsset, "code")}</div>
        </div>}
        <Errors />
      </Toolbar>
      <Tabs_s>
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header="Information">
            { currentAsset && <AssetSettings asset={currentAsset} charts={charts} readonly={readonly}/> }
          </TabPanel>
          <TabPanel header="Attributes">
            { currentAsset && <AssetAttributes tableId={'assetattribute'} data={attributedata} assetid={currentAsset['assetid']} readonly={readonly} /> }
          </TabPanel>
        </TabView>
      </Tabs_s>
    </div>
  )
}

export default AssetListDetail
